import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.css';

type Role = 'user' | 'assistant';

type Msg = {
  role: Role;
  content: string;
  sources?: string[]; // solo nombres de archivo
};

type Chat = {
  id: string;            // sessionId
  name: string;          // título visible en historial
  createdAt: number;     // epoch ms
  messages: Msg[];
};

type Health = 'loading' | 'ok' | 'error';

const LS_KEYS = {
  THEME: 'soporte.theme',
  BRAND: 'soporte.brand',
  BACKEND: 'soporte.backend',
  CHATS: 'soporte.chats',
  ACTIVE: 'soporte.active',       // sessionId activo (única que puede enviar)
  VIEW: 'soporte.view',           // conversación actualmente visible (puede ser distinta a ACTIVE)
  SIDEBAR: 'soporte.sidebar',     // abierto/cerrado
};

const DEFAULT_BRAND = '#1CAFE6';
const DEFAULT_BACKEND = 'https://chatbot.kkrot.com/';

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function fmtDate(d = new Date()) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getFullYear().toString() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    '-' +
    pad(d.getHours()) +
    pad(d.getMinutes())
  );
}
function guessTitle(msgs: Msg[]): string {
  const first = msgs.find(m => m.role === 'user')?.content?.trim() || '';
  if (first && first.length >= 6) return first.slice(0, 60);
  const a = msgs.find(m => m.role === 'assistant')?.content?.trim() || '';
  return (a || 'Nueva sesión').slice(0, 60);
}

/** Recorta una sección "Fuentes"/"Sources" solo si está al final del texto (más seguro). */
function stripTrailingSources(text: string): string {
  if (!text) return text;
  const tail = text.length > 1500 ? text.slice(-1500) : text;
  const re = /(?:^|\n)(?:[#>\-/_\s]*)(?:\*{0,3}|_{0,3})\s*(fuentes?|sources?)\s*(?:\*{0,3}|_{0,3})\s*[:：-]?\s*\n[\s\S]*$/i;
  const m = tail.match(re);
  if (!m || m.index === undefined) return text;
  const cutAt = text.length - tail.length + m.index;
  return text.slice(0, cutAt).trim();
}

/** Sanitiza texto del asistente para ocultar “Fuentes/Sources/Referencias/…”, con o sin adornos (#/*-_), y variantes. */
function sanitizeAnswer(s: string): string {
  if (!s) return s;
  let out = s;

  // 1) Bloques encabezados por Fuentes/Sources/Referencias/etc. (con #, *, -, >, /, _), hasta doble salto o fin.
  const blockHeadings =
    /(?:^|\n)[ \t>*\-#_/]*\*{0,3}[_#/\-\s]*\s*(?:fuentes?|sources?|referencias|references|citas|bibliograf[íi]a|archivos|files)\s*\*{0,3}\s*[:：-]?\s*\n(?:[\s\S]*?)(?=(?:\n{2,}|$))/gi;
  out = out.replace(blockHeadings, '\n');

  // 2) Líneas sueltas tipo "**Fuentes**:", "Sources - ...", "## Referencias", etc.
  const singleLine =
    /(?:^|\n)[ \t>*\-#_/]*\*{0,3}\s*(?:fuentes?|sources?|referencias|references|citas|bibliograf[íi]a|archivos|files)\s*\*{0,3}\s*(?:[:：\-]).*$/gim;
  out = out.replace(singleLine, '');

  // 3) Encabezados sin contenido explícito (ej. "## Fuentes" al final).
  const bareHeading =
    /(?:^|\n)[ \t>*\-#_/]*\*{0,3}\s*(?:fuentes?|sources?|referencias|references|citas|bibliograf[íi]a|archivos|files)\s*\*{0,3}\s*$/gim;
  out = out.replace(bareHeading, '');

  // 4) Limpieza de la sección al final por si quedó algo.
  out = stripTrailingSources(out);

  // 5) Quitar referencias tipo [1], [a], etc.
  out = out.replace(/\[[0-9a-zA-Z]{1,3}\]/g, '');

  // 6) Quitar "(Fuente: ...)" o "(Source: ...)" en línea.
  out = out.replace(/\(\s*(fuente|source)\s*:[^)]+?\)/gi, '');

  // 7) Quitar "Archivos: ..." en línea (por si el modelo lo usa así).
  out = out.replace(/(?:^|\n)\s*Archivos?\s*:\s*.*$/gim, '');

  return out.trim();
}

export default function App() {
  // ===== preferencias =====
  const [theme, setTheme] = useState<string>(() => localStorage.getItem(LS_KEYS.THEME) || 'dark');
  const [brand, setBrand] = useState<string>(() => localStorage.getItem(LS_KEYS.BRAND) || DEFAULT_BRAND);
  const [backendUrl, setBackendUrl] = useState<string>(() => localStorage.getItem(LS_KEYS.BACKEND) || DEFAULT_BACKEND);
  const [health, setHealth] = useState<Health>('loading');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(LS_KEYS.SIDEBAR);
    if (saved === '0' || saved === '1') return saved === '1';
    return window.innerWidth >= 960; // por defecto abierta en pantallas anchas
  });

  // ===== datos =====
  const [chats, setChats] = useState<Chat[]>(() => {
    try {
      const raw = localStorage.getItem(LS_KEYS.CHATS);
      return raw ? (JSON.parse(raw) as Chat[]) : [];
    } catch {
      return [];
    }
  });
  const [activeId, setActiveId] = useState<string>(() => localStorage.getItem(LS_KEYS.ACTIVE) || uid());
  const [viewId, setViewId] = useState<string>(() => localStorage.getItem(LS_KEYS.VIEW) || activeId);

  const activeChat = useMemo<Chat>(() => {
    const c = chats.find(x => x.id === activeId);
    return c ?? { id: activeId, name: 'Nueva sesión', createdAt: Date.now(), messages: [] };
  }, [chats, activeId]);
  const viewChat = useMemo<Chat>(() => {
    const c = chats.find(x => x.id === viewId);
    if (c) return c;
    if (viewId === activeId) return activeChat;
    return activeChat;
  }, [chats, viewId, activeChat, activeId]);

  // ===== editor =====
  const [draft, setDraft] = useState('');
  const [waiting, setWaiting] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  // ===== persistencia prefs =====
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(LS_KEYS.THEME, theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.style.setProperty('--brand', brand);
    localStorage.setItem(LS_KEYS.BRAND, brand);
  }, [brand]);
  useEffect(() => {
    localStorage.setItem(LS_KEYS.BACKEND, backendUrl);
  }, [backendUrl]);
  useEffect(() => {
    localStorage.setItem(LS_KEYS.SIDEBAR, sidebarOpen ? '1' : '0');
  }, [sidebarOpen]);

  // ===== persistencia datos =====
  useEffect(() => {
    localStorage.setItem(LS_KEYS.CHATS, JSON.stringify(chats));
  }, [chats]);
  useEffect(() => { localStorage.setItem(LS_KEYS.ACTIVE, activeId); }, [activeId]);
  useEffect(() => { localStorage.setItem(LS_KEYS.VIEW, viewId); }, [viewId]);

  // ===== UI scroll =====
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [viewChat.messages.length, waiting, viewId]);

  // ===== health =====
  useEffect(() => {
    let stop = false;
    const ping = async () => {
      setHealth('loading');
      try {
        const r = await fetch(new URL('/health', backendUrl).toString());
        if (!stop) setHealth(r.ok ? 'ok' : 'error');
      } catch {
        if (!stop) setHealth('error');
      }
    };
    ping();
    const t = setInterval(ping, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [backendUrl]);

  // ===== upsert sobre ACTIVE =====
  const upsertActive = (fn: (prev: Chat) => Chat) => {
    setChats(prev => {
      const idx = prev.findIndex(c => c.id === activeId);
      const base = idx === -1 ? { id: activeId, name: 'Nueva sesión', createdAt: Date.now(), messages: [] } : prev[idx];
      const next = fn(base);
      if (idx === -1) return [...prev, next];
      const cp = [...prev];
      cp[idx] = next;
      return cp;
    });
  };

  // ===== nueva sesión =====
  const handleNewSession = () => {
    if (activeChat.messages.length > 0) {
      setChats(prev =>
        prev.map(c => (c.id === activeId ? { ...c, name: guessTitle(c.messages) } : c)),
      );
    }
    const nid = uid();
    setActiveId(nid);
    setViewId(nid);
    setDraft('');
    setWaiting(false);
  };

  // ===== enviar (solo si viewId === activeId) =====
  const canSend = viewId === activeId;
  const send = async () => {
    const firstTurn = activeChat.messages.length === 0;
    const question = draft.trim();
    if (!question || waiting || !canSend) return;

    setDraft('');
    setWaiting(true);

    upsertActive(prev => ({
      ...prev,
      name: prev.messages.length ? prev.name : guessTitle([{ role: 'user', content: question }]),
      messages: [...prev.messages, { role: 'user', content: question }],
    }));

    try {
      const qForAPI = (firstTurn ? '<<FIRST_TURN:yes>>\n' : '') + question;
      const url = new URL('/query', backendUrl).toString();
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // IMPORTANTE: el backend espera solo q y session_id
        body: JSON.stringify({ q: qForAPI, session_id: activeId }),
      });

      let data: any = null;
      let ok = r.ok;
      let bodyText = '';
      try {
        // Intenta JSON; si falla, trata como texto para mostrarlo
        data = await r.json();
      } catch {
        try {
          bodyText = await r.text();
        } catch {
          bodyText = '';
        }
      }

      if (!ok) {
        const msg = `⚠️ Backend ${r.status} ${r.statusText}${bodyText ? ` — ${bodyText.slice(0, 500)}` : ''}`;
        upsertActive(prev => ({
          ...prev,
          messages: [...prev.messages, { role: 'assistant', content: msg, sources: [] }],
        }));
        return;
      }

      const rawAnswer = (data?.answer ?? bodyText ?? '').toString().trim();
      const sources = Array.isArray(data?.sources)
        ? data.sources.map((s: any) => (s?.name || s?.title || s?.path || s)?.toString())
        : [];

      const answerSan = sanitizeAnswer(rawAnswer);

      const finalAnswer =
        answerSan && answerSan.trim()
          ? answerSan
          : (rawAnswer ? rawAnswer : '⚠️ Respuesta vacía del backend.');

      upsertActive(prev => ({
        ...prev,
        name: guessTitle([...prev.messages, { role: 'assistant', content: finalAnswer }]),
        messages: [...prev.messages, { role: 'assistant', content: finalAnswer, sources }],
      }));
    } catch (e: any) {
      const msg = `⚠️ Error consultando el backend. ${e?.message ? `(${e.message})` : ''}`;
      upsertActive(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: msg, sources: [] }],
      }));
    } finally {
      setWaiting(false);
    }
  };

  // ===== acciones historial =====
  const renameChat = (id: string) => {
    const c = chats.find(x => x.id === id) || (id === activeId ? activeChat : undefined);
    if (!c) return;
    const v = prompt('Nuevo nombre:', c.name);
    if (!v) return;
    if (id === activeId) {
      upsertActive(prev => ({ ...prev, name: v }));
    } else {
      setChats(prev => prev.map(x => (x.id === id ? { ...x, name: v } : x)));
    }
  };

  const duplicateChat = (id: string) => {
    const c = id === activeId ? activeChat : chats.find(x => x.id === id);
    if (!c) return;
    const copy: Chat = {
      ...c,
      id: uid(),
      name: `${c.name} (copia)`,
      createdAt: Date.now(),
    };
    setChats(prev => [copy, ...prev]);
  };

  const clearChat = (id: string) => {
    if (!confirm('¿Vaciar mensajes de esta conversación?')) return;
    if (id === activeId) {
      upsertActive(prev => ({ ...prev, messages: [] }));
      if (viewId === id) setViewId(id);
    } else {
      setChats(prev => prev.map(c => (c.id === id ? { ...c, messages: [] } : c)));
    }
  };

  const deleteChat = (id: string) => {
    if (!confirm('¿Borrar esta conversación?')) return;
    if (id === activeId) {
      setChats(prev => prev.filter(c => c.id !== id));
      const nid = uid();
      setActiveId(nid);
      setViewId(nid);
      setDraft('');
      setWaiting(false);
    } else {
      setChats(prev => prev.filter(c => c.id !== id));
      if (viewId === id) setViewId(activeId);
    }
  };

  const exportTxt = (c: Chat) => {
    const lines: string[] = [];
    lines.push(`Soporte IA — ${c.name}`);
    lines.push(`SessionId: ${c.id}  Fecha: ${new Date(c.createdAt).toLocaleString()}`);
    lines.push('='.repeat(60));
    lines.push('');
    c.messages.forEach((m, i) => {
      lines.push(`${m.role === 'user' ? 'Tú' : 'Soporte IA'}:`);
      lines.push(m.content);
      if (m.role === 'assistant' && m.sources?.length) {
        lines.push('');
        lines.push('Archivos: ' + m.sources.join(' | '));
      }
      lines.push('');
      if (i < c.messages.length - 1) lines.push('-'.repeat(40));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${c.name.replace(/[^\w\d \-_.]/g, '').slice(0, 60) || 'chat'}_${fmtDate()}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const isActivePersisted = chats.some(c => c.id === activeId);

  return (
    <div className="app">
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(s => !s)}
        aria-label="Abrir/cerrar menú"
        title="Menú"
      >
        ☰
      </button>

      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="branding">
          <img src="/logo.png" alt="Soporte IA" />
          <span>Soporte IA - Estado del Chat: </span>
          <span className={`health health-${health}`} title="/health">
            {health === 'ok' ? 'OK' : health === 'loading' ? '…' : 'ERR'}
          </span>
        </div>

        <div className="prefs">
          <div className="row">
            <span className="badge">Elije un Tema</span>
            <div className="seg">
              <button className={theme === 'dark' ? 'on' : ''} onClick={() => setTheme('dark')}>
                Oscuro
              </button>
              <button className={theme === 'light' ? 'on' : ''} onClick={() => setTheme('light')}>
                Claro
              </button>
            </div>
          </div>

          <div className="row">
            <span className="badge">Color de Acento</span>
            <div className="brand">
              <div className="preview" style={{ background: brand }} />
              <input type="color" value={brand} onChange={e => setBrand(e.target.value)} />
              <div className="swatches">
                {[DEFAULT_BRAND, '#22c55e', '#f97316', '#ef4444', '#8b5cf6'].map(c => (
                  <button key={c} style={{ background: c }} onClick={() => setBrand(c)} aria-label={c} />
                ))}
              </div>
            </div>
          </div>

          <div className="row wrap">
            <button className="ghost" onClick={handleNewSession} title="Nueva sesión (sessionId nuevo)">
              Nuevo Chat
            </button>
            <button className="ghost" onClick={() => exportTxt(viewChat)} disabled={viewChat.messages.length === 0}>
              Exportar chat
            </button>
          </div>
        </div>

        <div className="hist">
          <div className="hist-header">
            <span>Historial</span>
            <button
              className="ghost danger"
              onClick={() => {
                if (chats.length === 0) return;
                if (!confirm('¿Borrar TODO el historial? (se descargara en automatico un .txt con el respaldo)')) return;
                const all: Chat = {
                  id: 'ALL',
                  name: 'Todos_los_chats',
                  createdAt: Date.now(),
                  messages: chats.flatMap((c, idx) => [
                    { role: 'assistant' as Role, content: `### ${idx + 1}. ${c.name}` },
                    ...c.messages,
                  ]),
                };
                exportTxt(all);
                setChats([]);
                if (viewId !== activeId) setViewId(activeId);
              }}
            >
              Limpiar historial
            </button>
          </div>

          <div className="hist-search">
            <input placeholder="Buscar en historial..." onChange={e => {
              const q = e.target.value.toLowerCase();
              document.querySelectorAll<HTMLDivElement>('.hist-item').forEach(n => {
                const t = (n.dataset.q || '').toLowerCase();
                n.style.display = t.includes(q) ? '' : 'none';
              });
            }} />
          </div>

          <div className="hist-list">
            {isActivePersisted && (
              <div
                className={`hist-item ${viewId === activeId ? 'viewing' : ''}`}
                data-q={activeChat.name}
                onClick={() => setViewId(activeId)}
              >
                <div className="title" title={activeChat.name}>{activeChat.name}</div>
                <div className="meta">
                  <span>{new Date(activeChat.createdAt).toLocaleString()}</span>
                  <span>{activeChat.messages.length} msg</span>
                </div>
                <div className="actions">
                  <button onClick={(e) => { e.stopPropagation(); renameChat(activeId); }}>Renombrar</button>
                  <button onClick={(e) => { e.stopPropagation(); duplicateChat(activeId); }}>Duplicar</button>
                  <button onClick={(e) => { e.stopPropagation(); exportTxt(activeChat); }}>Exportar</button>
                  <button onClick={(e) => { e.stopPropagation(); clearChat(activeId); }}>Vaciar</button>
                  <button className="danger" onClick={(e) => { e.stopPropagation(); deleteChat(activeId); }}>Borrar</button>
                </div>
              </div>
            )}

            {chats
              .filter(c => c.id !== activeId)
              .sort((a, b) => b.createdAt - a.createdAt)
              .map(c => (
                <div
                  key={c.id}
                  className={`hist-item ${viewId === c.id ? 'viewing' : ''}`}
                  data-q={c.name}
                  onClick={() => setViewId(c.id)}
                >
                  <div className="title" title={c.name}>{c.name}</div>
                  <div className="meta">
                    <span>{new Date(c.createdAt).toLocaleString()}</span>
                    <span>{c.messages.length} msg</span>
                  </div>
                  <div className="actions">
                    <button onClick={(e) => { e.stopPropagation(); renameChat(c.id); }}>Renombrar</button>
                    <button onClick={(e) => { e.stopPropagation(); duplicateChat(c.id); }}>Duplicar</button>
                    <button onClick={(e) => { e.stopPropagation(); exportTxt(c); }}>Exportar</button>
                    <button onClick={(e) => { e.stopPropagation(); clearChat(c.id); }}>Vaciar</button>
                    <button className="danger" onClick={(e) => { e.stopPropagation(); deleteChat(c.id); }}>Borrar</button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="chat-header">
          <div className="chat-title">{viewChat.name}</div>
          <div className="chat-meta">
            <span>{new Date(viewChat.createdAt).toLocaleString()}</span>
            <span>{viewChat.messages.length} msg</span>
            {viewId !== activeId && <span className="readonly-pill">Historial (solo lectura)</span>}
          </div>
        </div>

        <div className="chat" ref={chatRef}>
          {viewChat.messages.length === 0 && (
            <div className="msg">
              <div className="sender">Soporte IA</div>
              <div className="content">Pregúntame lo que quieras, por ejemplo: <em>"Cómo cambiar precios"</em>.</div>
            </div>
          )}

          {viewChat.messages.map((m, i) => (
            <div key={i} className={`msg ${m.role === 'user' ? 'user' : 'assistant'}`}>
              <div className="sender">{m.role === 'user' ? 'Tú' : 'Soporte IA'}</div>
              <div className="content">{m.content}</div>

              {m.role === 'assistant' && (
                <>
                  <div className="chips">
                    {(m.sources || []).map((s, k) => (
                      <span key={k} className="chip">{s}</span>
                    ))}
                  </div>
                  <div className="msg-actions">
                    <button onClick={() => navigator.clipboard.writeText(m.content)}>Copiar</button>
                  </div>
                </>
              )}
            </div>
          ))}

          {waiting && viewId === activeId && (
            <div className="typing">
              <span className="dot" /><span className="dot" /><span className="dot" />
              <span>Escribiendo...</span>
            </div>
          )}
        </div>

        <div className="composer">
          {viewId !== activeId ? (
            <div className="readonly">Historial (solo lectura)</div>
          ) : (
            <>
              <input
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Pregunta lo que quieras"
                disabled={waiting}
              />
              <button onClick={send} disabled={waiting || !draft.trim()}>
                Enviar
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
